// The settings surface: where a workspace's settings live (#258).
//
// **One section per path** (#804): Team & roles and Change policy, with a
// sub-navigation between them. The person's own passkeys and authenticator app are
// not here; they are Your account's (`AccountPage.tsx`).
//
// The second destination, and the reason this unit exists — a rail with one
// entry is not a rail, and Phase 2's screens each arrive in their own feature's
// unit (owner, 2026-09-07, #238) needing somewhere to land.
//
// **What it renders is what exists.** The three surfaces the cut names for this
// page are each behind a unit: the remediation policy is C3's, and is here
// (#260); the integration connections landed with #492 and are a Connections page
// of their own (#805), and the standing egress grants are C6's, landing with its unit. So the sections render
// the role catalogue the workspace answers to, and the policy the
// eligibility seam rules every proposed patch against. A section per unbuilt
// feature would be links to nowhere with extra steps, which is exactly what
// #231's rail refused to render.
//
// **The team panel mints invitations into this workspace** (#417) — a colleague
// joining the workspace you are in, with a role — and lists the ones still
// open. That is a team invitation; a *customer* invitation, which creates
// another organisation's workspace, is the Customers screen's, and the two
// are kept apart in wording as in code.

import { useEffect, useRef, useState, type FormEvent } from "react";

import { addressRefusal, messageOf } from "../api/http";
import {
  STEP_UP_PURPOSE as POLICY_STEP_UP_PURPOSE,
  currentRevision,
  type PolicyRead,
  type PolicyRevision,
  type PolicyScope,
  type StepUp,
} from "../api/policy";
import type { InviteRow } from "../api/customers";
import type { MemberRow, RemovalReach } from "../api/members";
import type { PasskeyChallenge, StepUpFactors } from "../api/passkeys";
import { roleHolds, type Role } from "../api/roles";
import { onOwnMembership, type SessionResponse } from "../api/session";
import { Chip, Field, Notice, PageIntro } from "./chrome";
import { SETTINGS_SECTIONS, visibleDestinations, type SettingsSection } from "./routes";
import {
  StepUpDialog,
  useStepUpAttempt,
  useStepUpFactors,
  type BeginPasskey,
  type FactorsReading,
} from "./StepUp";

/** The permissions on one role, sorted so two roles are comparable by eye. */
function Permissions({ permissions }: { permissions: string[] }) {
  if (permissions.length === 0) return <span className="muted">none</span>;
  return (
    <span className="row row--wrap">
      {[...permissions].sort().map((permission) => (
        <Chip key={permission}>{permission}</Chip>
      ))}
    </span>
  );
}

function Roles({ roles, error }: { roles: Role[] | null; error: string | null }) {
  return (
    <section className="panel" id="permissions" aria-label="Roles and permissions">
      <div className="panel__head">
        <h2>Roles</h2>
        {roles === null ? null : <Chip>{roles.length} shown</Chip>}
      </div>
      <div className="panel__body stack stack--tight">
        <p className="muted">Higher-ranked roles can manage roles below them.</p>
        {error === null ? null : <Notice tone="bad">{error}</Notice>}
        {roles === null && error === null ? (
          <p className="muted">Reading this workspace's roles…</p>
        ) : null}
        {roles !== null && roles.length === 0 ? (
          <p className="muted">No roles. That is a workspace this console cannot explain.</p>
        ) : null}
      </div>
      {roles !== null && roles.length > 0 ? (
        <div className="table-wrap" tabIndex={0}>
          <table className="table">
            <thead>
              <tr>
                <th>Role</th>
                <th>Rank</th>
                <th>Permissions</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr key={role.role_id}>
                  <td>
                    <strong>{role.name}</strong>{" "}
                    {role.custom ? <Chip>custom</Chip> : <span className="muted">system</span>}
                  </td>
                  <td>{role.level}</td>
                  <td>
                    <details>
                      <summary>{role.permissions.length} permissions</summary>
                      <Permissions permissions={role.permissions} />
                    </details>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

/** A scope as a person reads it: "any" on an empty axis, and every scope when there are several. */
export function scopeText(scopes: PolicyScope[]): string {
  if (scopes.length === 0) return "any account, any identifier";
  return scopes
    .map((scope) => {
      const accounts =
        scope.account_ids.length === 0 ? "any account" : `accounts ${scope.account_ids.join(", ")}`;
      const identifiers =
        scope.identifier_patterns.length === 0
          ? "any identifier"
          : `identifiers ${scope.identifier_patterns.join(", ")}`;
      return `${accounts} and ${identifiers}`;
    })
    .join("; or ");
}

const list = (text: string): string[] =>
  text
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item !== "");

/**
 * One property, one revision on, under a step-up.
 *
 * **It writes one scope.** A revision replaces the property's scopes, and this
 * form names at most one: empty on both axes is "any account, any identifier".
 * A policy that needs several scopes is written through the route directly,
 * which takes the list.
 *
 * The mode select offers the server's `modes` and nothing else, so `automatic`
 * (which the seam refuses) is never an option here.
 */
function PolicyForm({
  policy,
  onRevise,
  factors,
  beginPasskey,
  describedBy,
}: {
  policy: PolicyRead;
  /** The notice the save is described by: that no account applies what it lets through (#910). */
  describedBy?: string;
  onRevise: (revision: PolicyRevision, stepUp: StepUp) => Promise<void>;
  /** What the session holds, from `GET /auth/mfa` (#659, #729); unread, nothing is offered (#739). */
  factors: FactorsReading;
  beginPasskey?: BeginPasskey;
}) {
  const [resourceType, setResourceType] = useState("");
  const [rootProperty, setRootProperty] = useState("");
  const [mode, setMode] = useState(policy.modes[0] ?? "");
  const [accounts, setAccounts] = useState("");
  const [identifiers, setIdentifiers] = useState("");
  // The shared dialog's proof, dropped after every attempt (#729).
  const { proof, attempt, unproven, setProof, spent } = useStepUpAttempt();
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (proof === null) return;
    setError(null);
    setSaved(false);
    setBusy(true);
    const accountIds = list(accounts);
    const patterns = list(identifiers);
    try {
      await onRevise(
        {
          resource_type: resourceType.trim(),
          root_property: rootProperty.trim(),
          mode,
          scopes:
            accountIds.length === 0 && patterns.length === 0
              ? []
              : [{ account_ids: accountIds, identifier_patterns: patterns }],
          expected_revision: currentRevision(
            policy.policies,
            resourceType.trim(),
            rootProperty.trim(),
          ),
        },
        proof,
      );
      setSaved(true);
    } catch (failure) {
      setError(messageOf(failure));
    } finally {
      setBusy(false);
      spent();
    }
  };

  return (
    <form onSubmit={submit} className="panel__body settings-policy-form" aria-label="Edit remediation policy" aria-busy={busy}>
      <h3>Set one property</h3>
      {error === null ? null : <Notice tone="bad">{error}</Notice>}
      {saved ? <Notice tone="good">Policy revision saved.</Notice> : null}
      <div className="settings-fields">
        <Field
          id="policy-type"
          label="Resource type"
          placeholder="AWS::CloudWatch::Alarm"
          required
          value={resourceType}
          onChange={(event) => setResourceType(event.target.value)}
        />
        <Field
          id="policy-property"
          label="Root property"
          placeholder="Threshold"
          required
          value={rootProperty}
          onChange={(event) => setRootProperty(event.target.value)}
        />
        <p className="field">
          <label htmlFor="policy-mode" className="field__label">
            Mode
          </label>
          <select
            id="policy-mode"
            className="input"
            value={mode}
            onChange={(event) => setMode(event.target.value)}
          >
            {policy.modes.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </p>
        <Field
          id="policy-accounts"
          label="Accounts (comma-separated; empty for any)"
          value={accounts}
          onChange={(event) => setAccounts(event.target.value)}
        />
        <Field
          id="policy-identifiers"
          label="Identifier patterns (comma-separated; empty for any)"
          value={identifiers}
          onChange={(event) => setIdentifiers(event.target.value)}
        />
      </div>
      <StepUpDialog
        key={attempt}
        purpose={POLICY_STEP_UP_PURPOSE}
        factors={factors}
        disabled={busy}
        beginPasskey={beginPasskey}
        onProofChange={setProof}
        onError={setError}
      />
      <div className="actions">
        <button
          type="submit"
          className="btn btn--primary"
          disabled={busy || unproven || policy.modes.length === 0}
          aria-describedby={describedBy}
        >
          {busy ? "Saving revision…" : "Save a new revision"}
        </button>
      </div>
    </form>
  );
}

const APPLIES_NOWHERE_ID = "policy-applies-nowhere";

function RemediationPolicy({
  policy,
  error,
  onRevise,
  mayRevise,
  factors,
  beginPasskey,
}: {
  policy: PolicyRead | null;
  error: string | null;
  onRevise: (revision: PolicyRevision, stepUp: StepUp) => Promise<void>;
  mayRevise: boolean;
  factors: FactorsReading;
  beginPasskey?: BeginPasskey;
}) {
  const appliesNowhere =
    policy !== null && policy.policies.length > 0 && policy.applying_accounts === 0;
  return (
    <section className="panel" id="remediation" aria-label="Remediation policy">
      <div className="panel__head">
        <h2>Remediation policy</h2>
        {policy === null ? null : <Chip>{policy.policies.length} {policy.policies.length === 1 ? "property" : "properties"}</Chip>}
      </div>
      <div className="panel__body stack stack--tight">
        <p className="muted">
          Approval requires a person's confirmation; manual changes are performed by a person;
          denied blocks the change. Properties without a policy cannot be changed.
        </p>
        {error === null ? null : <Notice tone="bad">{error}</Notice>}
        {policy === null && error === null ? (
          <p className="muted">Reading this workspace's remediation policy…</p>
        ) : null}
        {appliesNowhere ? (
          <Notice id={APPLIES_NOWHERE_ID}>
            No account in this workspace lets the platform apply a change, so someone applies
            by hand every change you approve under these policies.
          </Notice>
        ) : null}
        {policy !== null && policy.policies.length === 0 ? (
          <p className="muted">
            No property has a policy, so no proposed change is eligible and no investigation is offered
            the tool that proposes one.
          </p>
        ) : null}
      </div>
      {policy !== null && policy.policies.length > 0 ? (
        <div className="table-wrap" tabIndex={0}>
          <table className="table">
            <thead>
              <tr>
                <th>Resource type</th>
                <th>Property</th>
                <th>Mode</th>
                <th>Scope</th>
                <th>Revision</th>
              </tr>
            </thead>
            <tbody>
              {policy.policies.map((row) => (
                <tr key={`${row.resource_type}/${row.root_property}`}>
                  <td>
                    <code>{row.resource_type}</code>
                  </td>
                  <td>
                    <code>{row.root_property}</code>
                  </td>
                  <td>
                    <Chip tone={row.mode === "approval" ? "good" : undefined}>{row.mode}</Chip>
                  </td>
                  <td>{scopeText(row.scopes)}</td>
                  <td>{row.revision}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {policy === null ? null : mayRevise ? <PolicyForm policy={policy} onRevise={onRevise} factors={factors} beginPasskey={beginPasskey} describedBy={appliesNowhere ? APPLIES_NOWHERE_ID : undefined} /> : (
        <p className="panel__body muted">You can read this policy. To change it, use a direct workspace membership with changes:manage permission.</p>
      )}
    </section>
  );
}

export function SettingsPage({
  section,
  session,
  roles,
  rolesError,
  policy,
  policyError,
  onRevisePolicy,
  members,
  membersError,
  onRemoveMember,
  readRemoval,
  invites,
  invitesError,
  onInviteMember,
  onRevokeInvite,
  stepUpFactors,
  beginPasskey,
}: {
  /** Which section this path is (#804): each is its own page. */
  section: SettingsSection;
  session: SessionResponse;
  /** The catalogue, or null while the read is in flight. */
  roles: Role[] | null;
  rolesError: string | null;
  /** The remediation policy, or null while the read is in flight. */
  policy: PolicyRead | null;
  policyError: string | null;
  onRevisePolicy: (revision: PolicyRevision, stepUp: StepUp) => Promise<void>;
  /** The workspace's members, or null while the read is in flight (#806). */
  members: MemberRow[] | null;
  membersError: string | null;
  /** Take a member out of this workspace, or yourself (#584); rejects with the refusal. */
  onRemoveMember: (member: MemberRow) => Promise<void>;
  /** What removing a member would stop, for the confirmation to say (#584). */
  readRemoval: (member: MemberRow) => Promise<RemovalReach>;
  /** The team's live invitations, or null while the read is in flight (#417). */
  invites: InviteRow[] | null;
  invitesError: string | null;
  onInviteMember: (body: { email: string; role: string }) => Promise<void>;
  onRevokeInvite: (inviteId: string) => Promise<void>;
  /** `GET /auth/mfa`'s factors, for the policy form's step-up (#659). */
  stepUpFactors: () => Promise<StepUpFactors>;
  /** Mint the assertion challenge for the policy form's passkey step-up (#729). */
  beginPasskey?: (purpose: string) => Promise<PasskeyChallenge>;
}) {
  const ownMembership = onOwnMembership(session);
  const permits = (permission: string) => roleHolds(roles ?? null, session.role, permission);
  const mayInvite = ownMembership && permits("members:invite");
  const factors = useStepUpFactors(stepUpFactors);
  const inWorkspace = session.workspace_id !== null;
  return (
    <>
      <PageIntro destination={section} />
      <div className="settings-layout">
        <SettingsNav session={session} section={section} />
        <div className="settings-body stack">
          {section === "team" ? (
            <>
              {inWorkspace ? (
                <Members
                  members={members}
                  error={membersError}
                  mayRemove={(member) => mayRemove(session, roles ?? null, member)}
                  onRemove={onRemoveMember}
                  readRemoval={readRemoval}
                />
              ) : null}
              {inWorkspace ? (
                <Team
                  invites={invites}
                  error={invitesError}
                  onInvite={onInviteMember}
                  onRevoke={onRevokeInvite}
                  mayInvite={mayInvite}
                />
              ) : null}
              <Roles roles={roles} error={rolesError} />
            </>
          ) : null}
          {section === "policy" ? (
            <RemediationPolicy policy={policy} error={policyError} onRevise={onRevisePolicy} mayRevise={ownMembership && permits("changes:manage")} factors={factors} beginPasskey={beginPasskey} />
          ) : null}
        </div>
      </div>
    </>
  );
}

/**
 * The workspace settings' own index (#804): each section is a page, and this says
 * which one you are on and where the others are. The sections are the registry's,
 * shown to whom the rail shows them.
 */
function SettingsNav({ session, section }: { session: SessionResponse; section: SettingsSection }) {
  const shown = visibleDestinations(session).filter((entry) =>
    (SETTINGS_SECTIONS as readonly string[]).includes(entry.id),
  );
  return (
    <nav className="settings-nav" aria-label="Workspace settings">
      <ul>
        {shown.map((entry) => (
          <li key={entry.id}>
            {entry.id === section ? (
              <span aria-current="page">{entry.label}</span>
            ) : (
              <a href={entry.path}>{entry.label}</a>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** What a session may do to one member's row (#584): leave, if it is their own; remove,
 * where their role holds `members:remove` and ranks no lower than the member's; or nothing.
 * The server's rule is the authority, and also refuses a member whose role holds what the
 * remover's does not; this offers nothing it can already tell would be refused. */
export function mayRemove(
  session: SessionResponse,
  roles: Role[] | null,
  member: MemberRow,
): "leave" | "remove" | null {
  if (!onOwnMembership(session)) return null;
  if (member.user_id === session.user.user_id) return "leave";
  if (!roleHolds(roles, session.role, "members:remove")) return null;
  const level = (id: string | null) => roles?.find((role) => role.role_id === id)?.level ?? 0;
  return level(member.role) <= level(session.role) ? "remove" : null;
}

const who = (member: MemberRow) => member.name ?? member.email;

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** What the confirmation says the removal stops (#584), in words. */
export function reachText(reach: RemovalReach | string | null): string {
  if (reach === null) return "Counting what stops working…";
  if (typeof reach === "string") return `What stops working could not be read: ${reach}`;
  if (reach.api_keys === 0 && reach.invitations === 0) return "No API key or open invitation stops working.";
  const parts = [
    reach.api_keys === 0 ? null : count(reach.api_keys, "API key", "API keys"),
    reach.invitations === 0 ? null : count(reach.invitations, "open invitation", "open invitations"),
  ].filter((part) => part !== null);
  return `${parts.join(" and ")} stop${reach.api_keys + reach.invitations === 1 ? "s" : ""} working.`;
}

/**
 * Who is in this workspace (#806), above the invitations of who is asked in. A refused
 * read says so here, in the API's words, and never reads as a workspace with nobody in it.
 *
 * **Removing one is two steps** (#584): Remove opens a confirmation that names the person,
 * and focus moves to it; Cancel hands focus back to the row's button, and a removal moves
 * it to the line that says it is done, since the row it came from is gone.
 */
function Members({
  members,
  error,
  mayRemove,
  onRemove,
  readRemoval,
}: {
  members: MemberRow[] | null;
  error: string | null;
  mayRemove: (member: MemberRow) => "leave" | "remove" | null;
  onRemove: (member: MemberRow) => Promise<void>;
  readRemoval: (member: MemberRow) => Promise<RemovalReach>;
}) {
  /** What the removal stops, once read; a string when the read was refused. */
  const [reach, setReach] = useState<RemovalReach | string | null>(null);
  const [confirming, setConfirming] = useState<MemberRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [returnTo, setReturnTo] = useState<string | null>(null);
  const prompt = useRef<HTMLParagraphElement>(null);
  const doneLine = useRef<HTMLParagraphElement>(null);
  const buttons = useRef(new Map<string, HTMLButtonElement>());
  useEffect(() => {
    if (confirming !== null) prompt.current?.focus();
  }, [confirming]);
  useEffect(() => {
    if (done !== null) doneLine.current?.focus();
  }, [done]);
  useEffect(() => {
    if (returnTo === null) return;
    buttons.current.get(returnTo)?.focus();
    setReturnTo(null);
  }, [returnTo]);
  const open = (member: MemberRow) => {
    setFailure(null);
    setDone(null);
    setReach(null);
    setConfirming(member);
    readRemoval(member).then(
      (read) => setReach(read),
      (refusal) => setReach(messageOf(refusal)),
    );
  };
  const cancel = () => {
    const member = confirming;
    setConfirming(null);
    setFailure(null);
    if (member !== null) setReturnTo(member.user_id);
  };
  const remove = async () => {
    if (confirming === null) return;
    setBusy(true);
    setFailure(null);
    try {
      await onRemove(confirming);
      const leaving = mayRemove(confirming) === "leave";
      setConfirming(null);
      setDone(leaving ? "You have left this workspace." : `${who(confirming)} was removed from this workspace.`);
    } catch (refusal) {
      setFailure(`${messageOf(refusal)} ${who(confirming)} is still a member.`);
    } finally {
      setBusy(false);
    }
  };
  const leaving = confirming !== null && mayRemove(confirming) === "leave";
  return (
    <section className="panel" id="members" aria-label="Workspace members">
      <div className="panel__head">
        <h2>Members</h2>
        {members === null || error !== null ? null : (
          <Chip>
            {members.length} member{members.length === 1 ? "" : "s"}
          </Chip>
        )}
      </div>
      {error === null ? null : (
        <div className="panel__body">
          <Notice tone="bad">{error}</Notice>
        </div>
      )}
      {members === null && error === null ? <p className="panel__body muted">Reading members…</p> : null}
      {error === null && members !== null && members.length > 0 ? (
        <div className="table-wrap" tabIndex={0} role="region" aria-label="Members of this workspace">
          <table className="table">
            <caption className="sr-only">Members of this workspace</caption>
            <thead>
              <tr>
                <th scope="col">Member</th>
                <th scope="col">Role</th>
                <th scope="col">Joined</th>
                <th scope="col">Last active</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.user_id}>
                  <td>
                    <strong>{member.name ?? member.email}</strong>
                    {member.name === null ? null : (
                      <>
                        <br />
                        <small className="muted">{member.email}</small>
                      </>
                    )}
                  </td>
                  <td>{member.role}</td>
                  <td>{member.joined_at.slice(0, 10)}</td>
                  <td>{member.last_active_at === null ? "Never" : member.last_active_at.slice(0, 10)}</td>
                  <td>
                    {mayRemove(member) === null ? null : (
                      <button
                        type="button"
                        className="btn btn--small btn--danger"
                        disabled={busy}
                        aria-expanded={confirming?.user_id === member.user_id}
                        ref={(button) => {
                          if (button) buttons.current.set(member.user_id, button);
                          else buttons.current.delete(member.user_id);
                        }}
                        onClick={() => open(member)}
                      >
                        {mayRemove(member) === "leave" ? (
                          <>
                            Leave<span className="sr-only"> this workspace</span>
                          </>
                        ) : (
                          <>
                            Remove<span className="sr-only"> {who(member)}</span>
                          </>
                        )}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {confirming === null ? null : (
        <div className="panel__body stack stack--tight" role="group" aria-label={leaving ? "Leave this workspace" : `Remove ${who(confirming)}`}>
          <p ref={prompt} tabIndex={-1}>
            {leaving ? (
              <>
                <strong>Leave this workspace?</strong> You lose access to it at once, and need a new invitation to come back.
              </>
            ) : (
              <>
                <strong>
                  Remove {who(confirming)}
                  {confirming.name === null ? null : ` (${confirming.email})`} from this workspace?
                </strong>{" "}
                They lose access at once. What they asked for stays waiting for others to answer, and the audit trail keeps what they did.
              </>
            )}
          </p>
          <p className="muted">{reachText(reach)}</p>
          {failure === null ? null : <Notice tone="bad">{failure}</Notice>}
          <div className="actions">
            <button type="button" className="btn btn--danger" disabled={busy} onClick={() => void remove()}>
              {busy ? "Removing…" : leaving ? "Leave this workspace" : `Remove ${who(confirming)}`}
            </button>
            <button type="button" className="btn" disabled={busy} onClick={cancel}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {done === null ? null : (
        <div className="panel__body">
          <Notice tone="good" focusRef={doneLine}>
            {done}
          </Notice>
        </div>
      )}
    </section>
  );
}

/** A colleague into this workspace: the team invitation, distinct from a customer's. */
function Team({
  invites,
  error,
  onInvite,
  onRevoke,
  mayInvite,
}: {
  invites: InviteRow[] | null;
  error: string | null;
  onInvite: (body: { email: string; role: string }) => Promise<void>;
  onRevoke: (inviteId: string) => Promise<void>;
  mayInvite: boolean;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const emailInput = useRef<HTMLInputElement>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFailure(null);
    setEmailError(null);
    setSentTo(null);
    setBusy(true);
    try {
      await onInvite({ email, role });
      setSentTo(email);
      setEmail("");
    } catch (refusal) {
      const refused = addressRefusal(refusal, "email");
      if (refused === null) setFailure(messageOf(refusal));
      else {
        setEmailError(refused);
        emailInput.current?.focus();
      }
    } finally {
      setBusy(false);
    }
  };
  const revoke = async (id: string) => {
    setFailure(null);
    setBusy(true);
    try {
      await onRevoke(id);
    } catch (error) {
      setFailure(`${messageOf(error)} The invitation is still listed; retry or ask a workspace administrator.`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="panel" id="team" aria-label="Team invitations">
      <div className="panel__head">
        <h2>Team</h2>
        {invites === null ? null : <Chip>{invites.length} invitation{invites.length === 1 ? "" : "s"} open</Chip>}
      </div>
      {mayInvite ? <form className="panel__body" onSubmit={submit} aria-busy={busy} data-testid="invite-member">
        <p className="muted">
          The role you choose holds in this workspace only. To serve another company&apos;s estate,
          use Customers instead.
        </p>
        {sentTo === null ? null : <Notice tone="good">An invitation is on its way to {sentTo}.</Notice>}
        <div className="settings-fields">
          <Field
            id="team-email"
            ref={emailInput}
            label="Colleague's email"
            name="email"
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            error={emailError}
          />
          <p className="field">
            <label htmlFor="team-role" className="field__label">
              Role
            </label>
            <select
              id="team-role"
              className="input"
              value={role}
              onChange={(event) => setRole(event.target.value)}
            >
              <option value="viewer">viewer</option>
              <option value="member">member</option>
              <option value="admin">admin</option>
              <option value="owner">owner</option>
            </select>
          </p>
        </div>
        <div className="actions">
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy ? "Inviting…" : "Invite to this workspace"}
          </button>
        </div>
      </form> : <p className="panel__body muted">To invite teammates, use a direct workspace membership with members:invite permission.</p>}
      {failure === null ? null : <div className="panel__body"><Notice tone="bad">{failure} Your entries are kept.</Notice></div>}
      {invites === null && error === null ? <p className="panel__body muted">Reading pending invitations…</p> : null}
      {invites?.length === 0 ? <p className="panel__body muted">No pending invitations.</p> : null}
      {error === null ? null : (
        <div className="panel__body">
          <Notice tone="bad">{error}</Notice>
        </div>
      )}
      {invites !== null && invites.length > 0 ? (
        <div className="table-wrap" tabIndex={0}>
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Invited</th>
                <th scope="col">Role</th>
                <th scope="col">By</th>
                <th scope="col">Expires</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {invites.map((invite) => (
                <tr key={invite.id}>
                  <td>
                    <strong>{invite.email}</strong>
                  </td>
                  <td>{invite.role}</td>
                  <td>{invite.invited_by}</td>
                  <td>
                    {invite.expired ? (
                      <Chip tone="bad">expired</Chip>
                    ) : (
                      invite.expires_at.slice(0, 10)
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn--small btn--danger"
                      disabled={busy || !mayInvite}
                      onClick={() => void revoke(invite.id)}
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
